import { BrandMark } from '@/components/brand-mark';
import { NavLink } from 'react-router';
import { hasRole } from '@fkwd-qc/shared';
import type { Role } from '@fkwd-qc/shared';
import type { ModuleDefinition, ModuleNavItem, NavBadge } from '@/modules';

/**
 * Props for the sidebar.
 *
 * @since 1.0.0
 */
export interface NavSidebarProps {
    modules: ModuleDefinition[];
    badgeCounts: Partial<Record<NavBadge, number>>;
    role: Role | null;
    statusLine: string;
    lastEventLabel: string;
    id?: string;
    isOpen?: boolean;
    onNavigate?: () => void;
}

/**
 * Which badges are loud. Open alerts are the only count worth a red mark.
 *
 * @since 1.0.0
 */
const LOUD_BADGES: NavBadge[] = ['open-alerts'];

/**
 * What each badge counts, read out after the number so a screen reader hears "3 open"
 * rather than a bare digit.
 *
 * @since 1.3.0
 */
const BADGE_SUFFIXES: Record<NavBadge, string> = {
    'open-alerts': 'open',
    'live-checks': 'running',
    'client-count': 'monitored',
};

/**
 * Decides whether a nav item is visible to a role.
 *
 * @since 1.0.0
 * @param item - the nav item
 * @param role - the signed-in user's role, or null when unknown
 * @returns whether to render the item
 */
function isItemVisible(item: ModuleNavItem, role: Role | null): boolean {
    if (item.minRole === undefined) {
        return true;
    }

    return role !== null && hasRole(role, item.minRole);
}

/**
 * The sidebar: a fixed column on a wide screen, a panel the topbar's menu button reveals on
 * a narrow one. Sections, items, badges and role gating come from the module registry.
 *
 * @since 1.0.0
 * @param props - the modules to render, badge counts, the signed-in role, the footer copy,
 *     and the id, open state and navigation callback the narrow layout uses
 * @returns the sidebar element
 */
export function NavSidebar({
    modules,
    badgeCounts,
    role,
    statusLine,
    lastEventLabel,
    id,
    isOpen = false,
    onNavigate,
}: NavSidebarProps) {
    const sections = [...modules].sort((first, second) => first.navSection.order - second.navSection.order);

    return (
        <nav aria-label="Sections" className={isOpen ? 'fkwd-nav fkwd-nav--open' : 'fkwd-nav'} id={id}>
            <div className="fkwd-brand">
                <BrandMark className="fkwd-brand" variant="on-dark" />
                <div className="fkwd-brand__sub">Quality control</div>
            </div>

            {sections.map((section) => {
                const items = section.items.filter((item) => isItemVisible(item, role));

                if (items.length === 0) {
                    return null;
                }

                return (
                    <div className="fkwd-nav-group" key={section.id}>
                        <div className="fkwd-nav-label">{section.navSection.label}</div>
                        {items.map((item) => {
                            const Icon = item.icon;
                            const count = item.badge === undefined ? undefined : badgeCounts[item.badge];
                            const isLoud = item.badge !== undefined && LOUD_BADGES.includes(item.badge);
                            const countClass = isLoud
                                ? 'fkwd-nav-item__count'
                                : 'fkwd-nav-item__count fkwd-nav-item__count--quiet';

                            return (
                                <NavLink
                                    className="fkwd-nav-item"
                                    end={item.path === '/'}
                                    key={item.id}
                                    onClick={onNavigate}
                                    to={item.path}
                                >
                                    <Icon aria-hidden="true" className="fkwd-nav-item__icon" size={14} />
                                    {item.label}
                                    {count === undefined || item.badge === undefined ? null : (
                                        <>
                                            {' '}
                                            <span className={countClass}>
                                                {count}{' '}
                                                <span className="fkwd-visually-hidden">
                                                    {BADGE_SUFFIXES[item.badge]}
                                                </span>
                                            </span>
                                        </>
                                    )}
                                </NavLink>
                            );
                        })}
                    </div>
                );
            })}

            <div className="fkwd-nav-foot">
                <div>{statusLine}</div>
                <div className="fkwd-nav-foot__uptime">{lastEventLabel}</div>
            </div>
        </nav>
    );
}
